import { EditOutlined } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES } from "../../utils/constants";
import { debounce } from "lodash";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import { capitalize, TrimText } from "../../utils/helpers";
import ViewEmailDetail from "./ViewBoosters";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { boostersServices } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { fDate } from "../../utils/formatTime";
import Iconify from "../../components/iconify";

export default function BoostersManagementList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState();
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalActiveBoosters, setTotalActiveBoosters] = useState(0);
  const [totalInactiveBoosters, setTotalInactiveBoosters] = useState(0);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearchFunction(query);
  };

  const debouncedSearchFunction = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
    }, 400),
    []
  );

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Title",
      sortable: true,
      selector: (row) => capitalize(TrimText(row.name, 25)),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    ...(user?.role !== ADMIN_ROLES.JUNIOR ? [{
      name: "Status",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setAction("status");
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
                    } the booster?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    }] : []),
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Tooltip title="Edit" placement="left">
              <IconButton
                color="primary"
                onClick={() => {
                  navigate(`/boosters-management/edit/${row?._id}`);
                }}
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchItem]);

  useEffect(() => {
    const body = {
      searchItem: debouncedSearch,
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    setIsLoading(true)
    setLoaderDelay(false);
    boostersServices.getAllBooster(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.result);
            setTotalPages(res?.data?.data?.totalRecords);

            // Calculate stats
            if (res?.data?.data?.result) {
              const boosters = res.data.data.result;
              setTotalActiveBoosters(boosters.filter(item => item.status === 1).length);
              setTotalInactiveBoosters(boosters.filter(item => item.status === 0).length);
            }

            setLoaderDelay(true);
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
        setLoaderDelay(true);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const res = await boostersServices.changeBoosterStatus({
        id,
        status: newStatus,
      });

      setCurrentStatus("");

      // Check if the API call was successful (status 200 or 201)
      if (res && (res.status === 200 || res.status === 201)) {
        const message = res?.data?.message || "Status updated successfully";
        toast.success(message, { toastId: "success" });

        // Update the local state immediately after successful API call
        setData(prevData =>
          prevData.map(booster =>
            booster._id === id
              ? { ...booster, status: newStatus }
              : booster
          )
        );

        // Update the stats
        if (newStatus === 1) {
          setTotalActiveBoosters(prev => prev + 1);
          setTotalInactiveBoosters(prev => prev - 1);
        } else {
          setTotalActiveBoosters(prev => prev - 1);
          setTotalInactiveBoosters(prev => prev + 1);
        }

        setShow(false);
      } else {
        // Only show error if the API call actually failed
        const errorMessage = res?.data?.message || "Failed to update status";
        toast.error(errorMessage, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  const renderBoosterCard = (booster, index) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={booster._id}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
          }
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
              boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 24, color: 'white' }} />
          </Avatar>
          <Stack direction="row" alignItems="center" spacing={1}>
            {user?.role !== ADMIN_ROLES.JUNIOR && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={booster.status === 1}
                      onClick={() => {
                        setShow(true);
                        setCurrentStatus(booster.status);
                        setStatusId(booster._id);
                        setAction("status");
                        setWarning(
                          `Are you sure you want to ${booster.status === 1 ? "deactivate" : "activate"
                          } the booster?`
                        );
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#4caf50',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#4caf50',
                        },
                      }}
                    />
                  }
                  label=""
                />
              </FormGroup>
            )}
            <Chip
              label={booster.status === 1 ? "Active" : "Inactive"}
              size="small"
              sx={{
                backgroundColor: booster.status === 1
                  ? 'rgba(76, 175, 80, 0.2)'
                  : 'rgba(244, 67, 54, 0.2)',
                color: booster.status === 1 ? '#4caf50' : '#f44336',
                fontWeight: 600,
                border: '1px solid',
                borderColor: booster.status === 1
                  ? 'rgba(76, 175, 80, 0.3)'
                  : 'rgba(244, 67, 54, 0.3)'
              }}
            />
          </Stack>
        </Stack>

        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            {capitalize(TrimText(booster.name, 20))}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {TrimText(booster.description, 60)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Created: {fDate(booster.createdAt)}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => {
              setModalShow(true);
              setViewId(booster._id);
            }}
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              }
            }}
          >
            View
          </Button>
          {user?.role !== ADMIN_ROLES.JUNIOR && (
            <Button
              size="small"
              variant="contained"
              startIcon={<EditOutlined />}
              onClick={() => navigate(`/boosters-management/edit/${booster._id}`)}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                }
              }}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Paper>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Boosters Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and monitor all boosters in your system
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Stats Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(0, 161, 221, 0.1)',
                    border: '1px solid rgba(0, 161, 221, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
                    {data?.length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Boosters
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, mb: 1 }}>
                    {totalActiveBoosters}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Active Boosters
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 700, mb: 1 }}>
                    {totalInactiveBoosters}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Inactive Boosters
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(156, 39, 176, 0.1)',
                    border: '1px solid rgba(156, 39, 176, 0.2)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                    {totalPages}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total Records
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <TableFilter
                  data={STATUS_OPTIONS_LIST}
                  value={status}
                  setValue={setStatus}
                  filterName="Status"
                />
                <Input
                  value={searchItem}
                  placeholder="Search boosters..."
                  onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          const cleared = "";
                          setSearchItem(cleared);
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No boosters found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria or create a new booster
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((booster, index) => renderBoosterCard(booster, index))}
                    </Grid>

                    {/* Pagination for Grid View */}
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} boosters
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* List View */
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    }
                  }}
                  columns={columns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={totalPages}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                    setPageSize(currentRowsPerPage);
                    setCurrentPage(currentPage);
                  }}
                  progressPending={isLoading || !loaderDelay}
                  progressComponent={<Loader />}
                  className="custom"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)} className="popupbox-statis">
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setShow(false)} color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Booster Dialog */}
      {modalShow ? (
        <ViewEmailDetail
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : null}
    </Container>
  );
}
